import React, { useEffect, useMemo, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const norm = (s) => String(s || "").split(":").pop().toUpperCase().replace(/[^A-Z0-9]/g, "");
const sym_match = (a, b) => {
  const x = norm(a), y = norm(b);
  if (!x || !y) return false;
  return x === y || x.startsWith(y) || y.startsWith(x);
};
export const money = (v) => `${Number(v) >= 0 ? "+" : "−"}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const sideOf = (t) => {
  const s = String(t ?? "").toUpperCase();
  if (s === "1" || s.includes("SELL")) return "SELL";
  return "BUY";
};
export const fmtPrice = (p) => {
  const n = Number(p);
  if (!Number.isFinite(n)) return "—";
  const d = n >= 1000 ? 2 : n >= 100 ? 2 : n >= 1 ? 4 : 5;
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};

// Polls open MT5 positions and returns the ones matching this pane's symbol + account-wide running P&L.
export const usePanePositions = (symbol) => {
  const [positions, setPositions] = useState(null);
  const [tick, setTick] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    const fetchP = async () => {
      try {
        const r = await fetch(`${API}/mt5/positions`, { credentials: "include" });
        if (!r.ok) { setPositions([]); return; }
        const d = await r.json();
        setPositions(d.positions || []);
        setTick((t) => t + 1);
      } catch { setPositions([]); }
    };
    fetchP();
    timer.current = setInterval(fetchP, 5000);
    return () => clearInterval(timer.current);
  }, []);

  return useMemo(() => {
    const pos = positions || [];
    const overall = pos.reduce((a, p) => a + (Number(p.profit) || 0), 0);
    const mine = pos.filter((p) => sym_match(p.symbol, symbol));
    const pair = mine.reduce((a, p) => a + (Number(p.profit) || 0), 0);
    return { loaded: positions !== null, all: pos, mine, pairPnl: pair, overallPnl: overall, pairName: norm(symbol), tick };
  }, [positions, symbol, tick]);
};

// Compact running-P&L box beside the symbol picker: pair side/open price/P&L + account running P&L.
const PanePnl = ({ data }) => {
  const { all, mine, pairPnl, overallPnl, pairName, tick } = data;
  if (!all || all.length === 0) return null;
  const hasPair = mine.length > 0;
  const first = mine[0];
  const lots = mine.reduce((a, p) => a + (Number(p.volume) || 0), 0);
  const sides = new Set(mine.map((p) => sideOf(p.type)));
  const side = sides.size === 1 ? [...sides][0] : "MIXED";

  return (
    <div className="ws-panepnl" data-testid="pane-pnl">
      <div className={`ws-panepnl-chip ${hasPair ? (pairPnl >= 0 ? "up" : "down") : "flat"}`} data-testid="pane-pnl-pair" title={`${pairName} floating P&L`}>
        <span className="ws-panepnl-lbl">{pairName || "PAIR"}</span>
        {hasPair && (
          <span className={`ws-panepnl-side ${side.toLowerCase()}`} data-testid="pane-pnl-side">
            {side}{mine.length > 1 ? ` ×${mine.length}` : ""} {lots ? `${lots.toFixed(2)}` : ""}
          </span>
        )}
        {hasPair && (
          <span className="ws-panepnl-open" data-testid="pane-pnl-open" title="Open price">
            @ {mine.length === 1 ? fmtPrice(first.price_open) : fmtPrice(mine.reduce((a, p) => a + Number(p.price_open || 0) * Number(p.volume || 0), 0) / (lots || 1))}
          </span>
        )}
        <b key={`p${tick}`} className="ws-panepnl-flash">{hasPair ? money(pairPnl) : "$0.00"}</b>
      </div>
      <div className={`ws-panepnl-chip acct ${overallPnl >= 0 ? "up" : "down"}`} data-testid="pane-pnl-account" title="Account running P&L (all open trades)">
        <span className="ws-panepnl-lbl">Running PnL</span>
        <b key={`a${tick}`} className="ws-panepnl-flash">{money(overallPnl)}</b>
      </div>
    </div>
  );
};

// Floating position tags over the chart — TradingView's embed is a cross-origin iframe, so the
// line can't be pinned to the price scale; this tag mirrors a TV position label instead.
export const PositionOverlay = ({ data }) => {
  const { mine, tick } = data;
  if (!mine || mine.length === 0) return null;
  return (
    <div className="ws-pos-overlay" data-testid="position-overlay">
      {mine.slice(0, 4).map((p) => {
        const side = sideOf(p.type);
        const pnl = Number(p.profit) || 0;
        return (
          <div key={p.ticket} className={`ws-pos-line ${pnl >= 0 ? "up" : "down"}`} data-testid={`position-line-${p.ticket}`}>
            <span className={`ws-pos-side ${side.toLowerCase()}`}>{side}</span>
            <span className="ws-pos-lots">{Number(p.volume || 0).toFixed(2)}</span>
            <span className="ws-pos-open">@ {fmtPrice(p.price_open)}</span>
            <span className="ws-pos-rule" aria-hidden />
            <b key={`o${tick}`} className="ws-pos-pnl ws-panepnl-flash">{money(pnl)}</b>
          </div>
        );
      })}
    </div>
  );
};

export default PanePnl;
